/**
 * QuotesReport — o relatório de orçamentos (KPIs, faturamento mensal, status,
 * top clientes, total do período).
 *
 * ─── O QUE MUDOU NESTE GRUPO ────────────────────────────────────────────────
 *
 * 1. **As vendas do PDV saíram.** A Venda Rápida grava a venda como `quotes` com
 *    `document_type='pedido'` e `status='completed'`, e este relatório não
 *    filtrava — então elas entravam em "Vendido", "Aprovados", "Ticket médio",
 *    no gráfico de faturamento, na pizza de status e em "Top clientes" (como
 *    "Consumidor não identificado"). Que era inconsistência e não decisão está
 *    provado no `DashboardPage`, que sempre excluiu com `.neq('document_type',
 *    'pedido')`. O filtro é feito na página (servidor + cache); aqui a receita do
 *    PDV **continua visível**, em card próprio e rotulada como fora dos KPIs —
 *    tirar sem mostrar em lugar nenhum trocaria um número inflado por dinheiro
 *    invisível.
 *
 * 2. **"Serviços mais vendidos" saiu.** Ele agrupava por `quotes.title`, contava
 *    DOCUMENTOS, não distinguia serviço de produto e caía num "Orçamento padrão"
 *    que virava o campeão de vendas de quem não preenche título. A agregação de
 *    verdade (por `quote_items`, com margem) é a aba **Itens**. Duas listas de
 *    "mais vendidos" discordando entre si seria pior que uma só.
 *
 * 3. **O rótulo do total vem do PRESET** (`@/lib/reportPeriod`), não de uma
 *    dedução por quantidade de dias — ver armadilha #45.
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BarChart3,
  TrendingUp,
  FileCheck,
  DollarSign,
  Users,
  Package,
  Store,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { DateRange } from '@/components/reports/DateRangeFilter';
import {
  AwaitingData,
  BlurOverlay,
  ListTail,
  WarningCard,
} from '@/components/reports/ReportGates';
import { MONEY_AXIS_WIDTH, moneyAxisTick } from '@/components/reports/axisFormat';
import {
  listLimitFor,
  ReportViewToggle,
  type ReportViewMode,
} from '@/components/reports/ReportViewToggle';
import { periodLabel, type ReportPeriodPreset } from '@/lib/reportPeriod';

export interface QuoteData {
  id: string;
  status: string | null;
  total: number | null;
  created_at: string;
  client_id?: string | null;
  client_name?: string;
  title?: string;
  /** 'pedido' = venda do PDV. A página já separa; fica no tipo para o guard. */
  document_type?: string | null;
  quote_items?: { product_id?: string | null }[];
  _offline_items?: { product_id?: string | null }[];
}

interface MonthlyData {
  month: string;
  total: number;
}

interface StatusData {
  name: string;
  value: number;
  color: string;
}

/** Quantos itens o RESUMO mostra nas listas. O Detalhado mostra tudo. */
const SUMMARY_LIMIT = 5;

// Sample data for free users (blurred preview)
const sampleMonthlyData: MonthlyData[] = [
  { month: 'Jul', total: 4500 },
  { month: 'Ago', total: 7200 },
  { month: 'Set', total: 5800 },
  { month: 'Out', total: 9100 },
  { month: 'Nov', total: 6300 },
  { month: 'Dez', total: 11500 },
];

const sampleStatusData: StatusData[] = [
  { name: 'Aprovados', value: 45, color: '#10B981' },
  { name: 'Pendentes', value: 30, color: '#F59E0B' },
  { name: 'Perdidos', value: 15, color: '#EF4444' },
];

/** Recorte próprio da Venda Rápida — o que saiu dos KPIs, mas não some da tela. */
export interface PdvSalesSummary {
  count: number;
  total: number;
}

interface QuotesReportProps {
  /** Orçamentos do período, **já sem as vendas do PDV** (a página separa). */
  filteredQuotes: QuoteData[];
  /** Vendas do PDV do mesmo período, contadas à parte. */
  pdvSales?: PdvSalesSummary;
  dateRange: DateRange;
  /**
   * Preset ativo do filtro — a fonte do rótulo do total. OPCIONAL: sem ele o
   * rótulo cai no classificador por quantidade de dias (`periodLabel`), que usa
   * os TETOS REAIS de cada preset (armadilha #45) e é o caminho da faixa
   * personalizada.
   */
  preset?: ReportPeriodPreset;
  viewMode: ReportViewMode;
  onViewModeChange: (mode: ReportViewMode) => void;
  /** A leitura bateu no teto de páginas: os números estão incompletos. */
  truncated?: boolean;
  /** Leva o usuário para a aba Itens (onde vive o ranking de verdade). */
  onOpenItems?: () => void;
  /** `reports_advanced.allowed` — falso borra tudo e mostra o CTA. */
  allowed: boolean;
  onUnlock: () => void;
}

export function QuotesReport({
  filteredQuotes,
  pdvSales = { count: 0, total: 0 },
  dateRange,
  preset,
  viewMode,
  onViewModeChange,
  truncated = false,
  onOpenItems,
  allowed,
  onUnlock,
}: QuotesReportProps) {
  const limit = listLimitFor(viewMode, SUMMARY_LIMIT);

  // Calculate summary stats for the selected period
  const stats = useMemo(() => {
    const approvedQuotes = filteredQuotes.filter(q =>
      q.status === 'approved' || q.status === 'completed'
    );

    const totalSold = approvedQuotes.reduce((sum, q) => sum + (q.total || 0), 0);
    const approvedCount = approvedQuotes.length;
    const averageTicket = approvedCount > 0 ? totalSold / approvedCount : 0;

    return {
      totalSold,
      approvedCount,
      averageTicket,
    };
  }, [filteredQuotes]);

  // Calculate monthly data for bar chart based on the date range
  const monthlyData = useMemo((): MonthlyData[] => {
    const months = eachMonthOfInterval({
      start: startOfMonth(dateRange.from),
      end: endOfMonth(dateRange.to)
    });

    return months.map(monthDate => {
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);

      const monthTotal = filteredQuotes
        .filter(q => {
          const createdAt = new Date(q.created_at);
          return (
            (q.status === 'approved' || q.status === 'completed') &&
            createdAt >= monthStart &&
            createdAt <= monthEnd
          );
        })
        .reduce((sum, q) => sum + (q.total || 0), 0);

      return {
        month: format(monthDate, 'MMM', { locale: ptBR }),
        total: monthTotal,
      };
    });
  }, [filteredQuotes, dateRange]);

  // Calculate status distribution for pie chart
  const statusData = useMemo((): StatusData[] => {
    const approved = filteredQuotes.filter(q => q.status === 'approved' || q.status === 'completed').length;
    const pending = filteredQuotes.filter(q => q.status === 'pending').length;
    const lost = filteredQuotes.filter(q => q.status === 'cancelled' || q.status === 'rejected').length;

    return [
      { name: 'Aprovados', value: approved, color: '#10B981' },
      { name: 'Pendentes', value: pending, color: '#F59E0B' },
      { name: 'Perdidos', value: lost, color: '#EF4444' },
    ].filter(item => item.value > 0);
  }, [filteredQuotes]);

  // Ranking COMPLETO de clientes — quem corta é a tela, conforme o modo.
  const topClients = useMemo(() => {
    const clientTotals: Record<string, number> = {};

    filteredQuotes
      .filter(q => q.status === 'approved' || q.status === 'completed')
      .forEach(q => {
        const name = q.client_name || 'Sem cliente';
        clientTotals[name] = (clientTotals[name] || 0) + (q.total || 0);
      });

    return Object.entries(clientTotals)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [filteredQuotes]);

  const shownClients = useMemo(() => topClients.slice(0, limit), [topClients, limit]);

  /** Lista linha a linha, só no modo Detalhado (a mais recente primeiro). */
  const detailedQuotes = useMemo(() => {
    if (viewMode !== 'detailed') return [];
    // A página já entrega a classificação escolhida (e o cache offline usa o
    // mesmo comparador). Reordenar por data aqui apagaria a escolha do usuário.
    return filteredQuotes.slice(0, limit);
  }, [filteredQuotes, viewMode, limit]);

  const hasMonthlyData = monthlyData.some(d => d.total > 0);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatCurrencyShort = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  const Gate = ({ children }: { children: React.ReactNode }) => (
    <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
      {children}
    </BlurOverlay>
  );

  return (
    <div className="space-y-6">
      {truncated && allowed && (
        <WarningCard>
          O período tem orçamentos demais e a leitura foi limitada. Os números abaixo estão
          incompletos — escolha um período menor.
        </WarningCard>
      )}

      {allowed && (
        <ReportViewToggle
          value={viewMode}
          onChange={onViewModeChange}
          hint={`${filteredQuotes.length} ${filteredQuotes.length === 1 ? 'orçamento' : 'orçamentos'} no período`}
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        {/* Total Vendido */}
        <Card>
          <CardContent className="p-3">
            <Gate>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/30">
                    <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Vendido</p>
                <p className="text-base font-bold text-emerald-600 truncate">
                  {allowed ? formatCurrencyShort(stats.totalSold) : 'R$ 8,5K'}
                </p>
              </div>
            </Gate>
          </CardContent>
        </Card>

        {/* Orçamentos Aprovados */}
        <Card>
          <CardContent className="p-3">
            <Gate>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-950/30">
                    <FileCheck className="h-3.5 w-3.5 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Aprovados</p>
                <p className="text-base font-bold text-blue-600">
                  {allowed ? stats.approvedCount : '12'}
                </p>
              </div>
            </Gate>
          </CardContent>
        </Card>

        {/* Ticket Médio */}
        <Card>
          <CardContent className="p-3">
            <Gate>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-950/30">
                    <TrendingUp className="h-3.5 w-3.5 text-purple-600" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Valor médio</p>
                <p className="text-base font-bold text-purple-600 truncate">
                  {allowed ? formatCurrencyShort(stats.averageTicket) : 'R$ 710'}
                </p>
              </div>
            </Gate>
          </CardContent>
        </Card>
      </div>

      {/* Venda Rápida (PDV): fora dos KPIs acima, mas NUNCA invisível. */}
      {allowed && pdvSales.count > 0 && (
        <Card className="bg-muted/30">
          <CardContent className="flex items-center justify-between gap-3 p-3">
            <div className="flex min-w-0 items-center gap-2">
              <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Vendas no balcão</p>
                <p className="text-[11px] leading-snug text-muted-foreground/70">
                  {pdvSales.count} {pdvSales.count === 1 ? 'venda' : 'vendas'} no período. Não
                  entram nos números acima: venda rápida não é orçamento.
                </p>
              </div>
            </div>
            <p className="shrink-0 text-lg font-bold text-emerald-600">
              {formatCurrencyShort(pdvSales.total)}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Bar Chart - Faturamento Mensal */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Faturamento mensal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            <div className="h-64">
              {allowed ? (
                hasMonthlyData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    {/* margin.left 0 + width explícito + tick sem centavos: com
                        `left: -10` e um tick "R$ 750,00" o "R$" saía CORTADO em
                        360px (armadilha #43 — a falha latente que estava
                        registrada justamente para este gráfico). */}
                    <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12 }}
                        className="text-muted-foreground"
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={moneyAxisTick}
                        width={MONEY_AXIS_WIDTH}
                        className="text-muted-foreground"
                      />
                      <Tooltip
                        formatter={(value: number) => [formatCurrency(value), 'Faturado']}
                        labelFormatter={(label) => `Mês: ${label}`}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar
                        dataKey="total"
                        fill="hsl(var(--primary))"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <AwaitingData />
                )
              ) : (
                // Sample data for free users (will be blurred)
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sampleMonthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12 }}
                      className="text-muted-foreground"
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={moneyAxisTick}
                      width={MONEY_AXIS_WIDTH}
                      className="text-muted-foreground"
                    />
                    <Bar
                      dataKey="total"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Gate>
        </CardContent>
      </Card>

      {/* Pie Chart - Status dos Orçamentos */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileCheck className="h-4 w-4" />
            Status dos orçamentos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            <div className="h-64">
              {allowed ? (
                statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      {/*
                        SÓ o percentual no rótulo — o NOME já está na <Legend>
                        logo abaixo, com a mesma cor da fatia.

                        Medido no aparelho a 360px: com o nome junto, o rótulo
                        media 110px e a pizza só tem 278px de largura, então os
                        dois extremos saíam CORTADOS ("Pendentes 79%" começava em
                        x=-9; "Aprovados 21%" terminava em x=370). Não era
                        vazamento estético: era texto ilegível nas duas pontas.
                        Família #43/#51/#84 — rótulo de gráfico só se valida
                        medindo, nunca no `tsc` nem no jsdom.
                      */}
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, name: string) => [value, name]}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => <span className="text-sm">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <AwaitingData />
                )
              ) : (
                // Sample data for free users (will be blurred)
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sampleStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {sampleStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => <span className="text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Gate>
        </CardContent>
      </Card>

      {/* Top Clients */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            {viewMode === 'detailed' ? 'Clientes do período' : 'Top clientes'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed && topClients.length > 0 ? (
              <div className="space-y-2">
                {shownClients.map((client, index) => (
                  <div key={client.name} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-4">{index + 1}.</span>
                      <span className="text-sm font-medium truncate max-w-[150px]">{client.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-primary">{formatCurrencyShort(client.total)}</span>
                  </div>
                ))}
                <ListTail total={topClients.length} shown={shownClients.length} noun="cliente" />
              </div>
            ) : allowed ? (
              <AwaitingData />
            ) : (
              <div className="space-y-2">
                {['Cliente A', 'Cliente B', 'Cliente C'].map((name, i) => (
                  <div key={name} className="flex items-center justify-between py-2 border-b last:border-0">
                    <span className="text-sm">{name}</span>
                    <span className="text-sm">R$ {(5000 - i * 1000).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* Lista linha a linha — o que o modo Detalhado acrescenta aqui. */}
      {allowed && viewMode === 'detailed' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Todos os orçamentos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {detailedQuotes.length === 0 ? (
              <AwaitingData hint="Nenhum orçamento no período selecionado" />
            ) : (
              <>
                {detailedQuotes.map((quote) => (
                  <div
                    key={quote.id}
                    className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {quote.title || 'Sem título'}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {quote.client_name || 'Sem cliente'} ·{' '}
                        {format(new Date(quote.created_at), 'dd/MM/yy', { locale: ptBR })} ·{' '}
                        {QUOTE_STATUS_LABELS[quote.status] ?? quote.status}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold">
                      {formatCurrencyShort(quote.total || 0)}
                    </span>
                  </div>
                ))}
                <ListTail
                  total={filteredQuotes.length}
                  shown={detailedQuotes.length}
                  noun="orçamento"
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* O ranking por ITEM mora na aba Itens — aqui fica só o caminho, para não
          existirem duas listas de "mais vendidos" com contas diferentes. */}
      {allowed && onOpenItems && (
        <Card>
          <CardContent className="flex items-center justify-between gap-3 p-4">
            <div className="flex min-w-0 items-center gap-2">
              <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-xs leading-snug text-muted-foreground">
                O ranking do que você mais vende agora é por item de verdade, com margem.
              </p>
            </div>
            <Button variant="outline" size="sm" className="shrink-0" onClick={onOpenItems}>
              Ver itens
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Total Stats */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <Gate>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total {periodLabel(dateRange, preset)}</p>
                <p className="text-2xl font-bold">
                  {allowed
                    ? formatCurrency(
                        filteredQuotes
                          .filter(q => q.status === 'approved' || q.status === 'completed')
                          .reduce((sum, q) => sum + (q.total || 0), 0)
                      )
                    : 'R$ 44.400,00'
                  }
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Orçamentos</p>
                <p className="text-2xl font-bold">{allowed ? filteredQuotes.length : '90'}</p>
              </div>
            </div>
          </Gate>
        </CardContent>
      </Card>
    </div>
  );
}

const QUOTE_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  rejected: 'Rejeitado',
};
